/**
 * AYU Check-In Wizard
 * Kurzer Fragebogen (18 Fragen) für den aktuellen funktionalen Zustand.
 * Wird alle 3-4 Monate ausgefüllt.
 * Eine Frage pro Seite, Fortschrittsbalken, AYU Garden Design.
 */
import { useState, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  Activity,
  Heart,
  Moon,
  Brain,
  Sparkles,
  Sun,
} from "lucide-react";
import {
  CHECK_IN_QUESTIONS,
  calculateFunctionalState,
  type CheckInAnswer,
  type CheckInQuestion,
  type FunctionalState,
} from "@shared/checkInSchema";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "Energie & Vitalität": <Activity className="w-5 h-5" />,
  "Verdauung & Stoffwechsel": <Heart className="w-5 h-5" />,
  "Schlaf & Erholung": <Moon className="w-5 h-5" />,
  "Stress & Emotionen": <Brain className="w-5 h-5" />,
  "Körper & Haut": <Sparkles className="w-5 h-5" />,
  "Saisonale Faktoren": <Sun className="w-5 h-5" />,
};

export default function AppCheckIn() {
  const { client, refetch } = useClientAuth();
  const [, navigate] = useLocation();
  const { t } = useI18n();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, CheckInAnswer>>({});
  const [showResult, setShowResult] = useState(false);
  const [functionalState, setFunctionalState] = useState<FunctionalState | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const saveCheckIn = trpc.clientAuth.saveCheckIn.useMutation();

  const questions = CHECK_IN_QUESTIONS;
  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const progress = ((currentIndex + 1) / totalQuestions) * 100;

  const currentAnswer = answers[currentQuestion?.id];
  const hasAnswer = currentAnswer !== undefined && currentAnswer.value !== "" &&
    (Array.isArray(currentAnswer.value) ? currentAnswer.value.length > 0 : true);

  // Kategorie-Wechsel erkennen
  const currentCategory = currentQuestion?.category;
  const prevCategory = currentIndex > 0 ? questions[currentIndex - 1]?.category : null;
  const isNewCategory = currentCategory !== prevCategory;

  const setAnswer = useCallback((questionId: string, value: string | number | string[]) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { questionId, value },
    }));
  }, []);

  const handleMultiSelect = useCallback(
    (questionId: string, value: string) => {
      const current = (answers[questionId]?.value as string[]) || [];
      // "none" / "Keine" deselektiert alles andere
      if (value === "none" || value === "Keine Probleme" || value === "Keine besonderen Beschwerden" || value === "Keine wesentlichen Veränderungen" || value === "Kein besonderer Stress") {
        setAnswer(questionId, [value]);
        return;
      }
      // Andere Auswahl entfernt "none"
      const filtered = current.filter(
        (v) => v !== "none" && v !== "Keine Probleme" && v !== "Keine besonderen Beschwerden" && v !== "Keine wesentlichen Veränderungen" && v !== "Kein besonderer Stress"
      );
      if (filtered.includes(value)) {
        const newVal = filtered.filter((v) => v !== value);
        setAnswer(questionId, newVal);
      } else {
        setAnswer(questionId, [...filtered, value]);
      }
    },
    [answers, setAnswer]
  );

  const goNext = useCallback(() => {
    if (currentIndex < totalQuestions - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((i) => i + 1);
        setIsTransitioning(false);
      }, 200);
    } else {
      // Fertig — Funktionalen Zustand berechnen
      const allAnswers = Object.values(answers);
      const state = calculateFunctionalState(allAnswers);
      setFunctionalState(state);
      setShowResult(true);

      // Speichern
      saveCheckIn.mutate(
        { answers: allAnswers, functionalState: state },
        {
          onSuccess: () => {
            refetch();
          },
        }
      );
    }
  }, [currentIndex, totalQuestions, answers, saveCheckIn, refetch]);

  const goBack = useCallback(() => {
    if (currentIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((i) => i - 1);
        setIsTransitioning(false);
      }, 200);
    }
  }, [currentIndex]);

  // ─── Ergebnis-Screen ───
  if (showResult && functionalState) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#6B7B3A]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#6B7B3A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C2C2C] font-serif">
              {t("checkin.abgeschlossen")}
            </h1>
            <p className="text-[#6B6B6B] mt-2">
              {t("checkin.zustandErfasst")}
            </p>
          </div>

          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-[#2C2C2C] mb-4">{t("checkin.aktuellerZustand")}</h3>

              <div className="space-y-3">
                {[
                  { label: t("checkin.energieLabel"), value: functionalState.energy, color: "#D4A853" },
                  { label: t("checkin.verdauungLabel"), value: functionalState.digestion, color: "#6B7B3A" },
                  { label: t("checkin.schlafLabel"), value: functionalState.sleep, color: "#5B6BAA" },
                  { label: t("checkin.stressLabel"), value: 10 - functionalState.stress, color: "#AA5B5B", invertLabel: true },
                  { label: t("checkin.emotionaleBalance"), value: functionalState.emotionalBalance, color: "#8B6BAA" },
                  { label: t("checkin.hautgesundheit"), value: functionalState.skinHealth, color: "#AA8B5B" },
                  { label: t("checkin.immunstaerke"), value: functionalState.immunity, color: "#5BAA6B" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span className="text-sm text-[#6B6B6B] w-36 shrink-0">{item.label}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${(item.value / 10) * 100}%`,
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-[#2C2C2C] w-8 text-right">
                      {item.invertLabel ? `${Math.round(10 - item.value)}` : Math.round(item.value)}/10
                    </span>
                  </div>
                ))}
              </div>

              {functionalState.dominantImbalance && (
                <div className="mt-4 p-3 bg-[#D4A853]/10 rounded-lg">
                  <p className="text-sm text-[#2C2C2C]">
                    <span className="font-medium">{t("checkin.fokusbereich")}</span>{" "}
                    {functionalState.dominantImbalance}
                  </p>
                </div>
              )}

              <p className="text-sm text-[#6B6B6B] mt-4">{functionalState.summary}</p>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/app")}
              className="flex-1 bg-[#6B7B3A] hover:bg-[#5A6A30] text-white"
            >
              {t("checkin.zumDashboard")}
            </Button>
            <Button
              onClick={() => navigate("/app/sessions")}
              variant="outline"
              className="flex-1 border-[#6B7B3A] text-[#6B7B3A]"
            >
              {t("checkin.sessionsStarten")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Frage-Screen ───
  return (
    <div className="min-h-screen bg-[#FAF6F0] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#FAF6F0]/95 backdrop-blur-sm border-b border-[#E8E0D4] px-4 py-3">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={goBack}
              disabled={currentIndex === 0}
              className="p-1 text-[#6B6B6B] disabled:opacity-30 hover:text-[#2C2C2C] transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-[#6B6B6B]">
              {currentIndex + 1} / {totalQuestions}
            </span>
            <div className="w-7" />
          </div>
          {/* Progress Bar */}
          <div className="h-1.5 bg-[#E8E0D4] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#6B7B3A] to-[#D4A853] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div
          className={`max-w-lg w-full transition-all duration-200 ${
            isTransitioning ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"
          }`}
        >
          {/* Kategorie-Header (bei Wechsel) */}
          {isNewCategory && (
            <div className="flex items-center gap-2 mb-4 text-[#6B7B3A]">
              {CATEGORY_ICONS[currentCategory] || <Activity className="w-5 h-5" />}
              <span className="text-sm font-medium uppercase tracking-wider">
                {currentCategory}
              </span>
            </div>
          )}

          <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6 font-serif">
            {currentQuestion.text}
          </h2>

          {/* Frage-Typ: Range/Slider */}
          {currentQuestion.type === "range" && (
            <div className="space-y-6">
              <div className="px-2">
                <Slider
                  value={[Number(currentAnswer?.value) || currentQuestion.min || 1]}
                  onValueChange={([v]) => setAnswer(currentQuestion.id, v)}
                  min={currentQuestion.min || 1}
                  max={currentQuestion.max || 10}
                  step={currentQuestion.step || 1}
                  className="w-full"
                />
              </div>
              <div className="flex justify-between text-sm text-[#6B6B6B]">
                <span>{currentQuestion.minLabel}</span>
                <span className="text-2xl font-bold text-[#D4A853]">
                  {Number(currentAnswer?.value) || currentQuestion.min || 1}
                </span>
                <span>{currentQuestion.maxLabel}</span>
              </div>
            </div>
          )}

          {/* Frage-Typ: Radio */}
          {currentQuestion.type === "radio" && (
            <div className="space-y-3">
              {currentQuestion.options?.map((opt) => {
                const isSelected = currentAnswer?.value === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setAnswer(currentQuestion.id, opt.value)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? "border-[#6B7B3A] bg-[#6B7B3A]/5 shadow-sm"
                        : "border-[#E8E0D4] bg-white hover:border-[#D4A853]/50 hover:bg-[#FAF6F0]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                          isSelected ? "border-[#6B7B3A] bg-[#6B7B3A]" : "border-[#C8C0B4]"
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`text-sm ${isSelected ? "text-[#2C2C2C] font-medium" : "text-[#6B6B6B]"}`}>
                        {opt.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Frage-Typ: Multiselect */}
          {currentQuestion.type === "multiselect" && (
            <div className="space-y-3">
              {currentQuestion.options?.map((opt) => {
                const selectedValues = (currentAnswer?.value as string[]) || [];
                const isSelected = selectedValues.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleMultiSelect(currentQuestion.id, opt.value)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? "border-[#6B7B3A] bg-[#6B7B3A]/5 shadow-sm"
                        : "border-[#E8E0D4] bg-white hover:border-[#D4A853]/50 hover:bg-[#FAF6F0]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                          isSelected ? "border-[#6B7B3A] bg-[#6B7B3A]" : "border-[#C8C0B4]"
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`text-sm ${isSelected ? "text-[#2C2C2C] font-medium" : "text-[#6B6B6B]"}`}>
                        {opt.label}
                      </span>
                    </div>
                  </button>
                );
              })}
              <p className="text-xs text-[#9B9B9B] mt-2">{t("checkin.mehrfachauswahl")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer mit Weiter-Button */}
      <div className="sticky bottom-0 bg-[#FAF6F0]/95 backdrop-blur-sm border-t border-[#E8E0D4] p-4">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={goNext}
            disabled={!hasAnswer || saveCheckIn.isPending}
            className="w-full bg-[#6B7B3A] hover:bg-[#5A6A30] text-white disabled:opacity-40 h-12 text-base"
          >
            {saveCheckIn.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : currentIndex === totalQuestions - 1 ? (
              <>
                <Check className="w-5 h-5 mr-2" />
                {t("checkin.abschliessen")}
              </>
            ) : (
              <>
                {t("checkin.weiter")}
                <ChevronRight className="w-5 h-5 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
